// src/scene/wall.ts
import * as THREE from 'three';
import type { Manifest, Work } from '../manifest';
import { pick } from '../i18n';
import { wallLayout, type Band } from './wallLayout';
import { outerFrameTexture, ringTexture } from './card';
import { FOCUS_UNIFORMS, FOCUS_DECL } from './focus';

const ART_Z = 0.006;    // artwork image, recessed in the window
const RING_Z = 0.010;   // keyline + contact-shadow overlay, in front of the art

export interface WorkPlane {
  root: THREE.Group;
  cardMesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  ringMesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  work: Work;
  position: THREE.Vector3;
  baseScale: THREE.Vector3;
  baseQuat: THREE.Quaternion;
  state: 'empty' | 'loading' | 'ready';
}

export interface WallHandle {
  group: THREE.Group;
  bands: Band[];
  planes: WorkPlane[];
  width: number;
  height: number;
}

// --- Architectural structure: raised white panels on a dark recess wall. ---
// No real lights in the scene, so depth is faked with baked tone: the base
// wall is the dark recess, panels are proud + bright, and the gaps between
// them (band grooves, the strip above the rail) read as shadow-gap reveals.
const PANEL_X = -0.012;        // panel face (proud of the recess at -0.05, behind works at x≥0)
const BOARD_X = -0.006;        // baseboard sticks out furthest
const BASEBOARD_TOP = 0.36;    // baseboard height at the foot of the wall
const PANEL_INSET_Y = 0.3;     // reveal margin above/below each band → ~0.8 groove between clusters
const PANEL_OVERHANG = 0.3;    // panels run this far past the work width on each side

function mkTex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Dark recess behind the panels — what shows through the reveal grooves. */
function wallRecessTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 8; c.height = 256;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0.0, '#0b0b0d');
  g.addColorStop(0.5, '#161618');
  g.addColorStop(1.0, '#0b0b0d');
  ctx.fillStyle = g; ctx.fillRect(0, 0, 8, 256);
  return mkTex(c);
}

/** Procedural plaster panel surface. No scene lights, so relief is faked by
 *  embossing world-space value-noise against an implied top-left key light;
 *  a broad mottle + fine grain + soft per-panel edge falloff keep it from
 *  reading flat. On top of that, a slow vertical rhythm of soft warm LIGHT
 *  POOLS is baked in (implied ceiling spots): the wall brightens and warms in
 *  periodic centred pools and dims/cools between them, so works sit in lit
 *  pools rather than under flat illumination. Fog chunks are wired in so
 *  panels still fade into the dark. */
function panelMaterial(width: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    fog: true,
    defines: { USE_FOG: '', FOG_EXP2: '' },
    // Merge clones, so merge the static fog uniforms first, then spread the
    // LIVE shared focus uniforms in by reference — main.ts writes uFocusY once
    // per frame and every panel's pool tracks the viewer.
    uniforms: {
      ...THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        { uWidth: { value: width } },
      ]),
      ...FOCUS_UNIFORMS,
    },
    vertexShader: /* glsl */ `
      #include <common>
      #include <fog_pars_vertex>
      varying vec3 vWorld;
      varying vec2 vUvP;
      void main() {
        vUvP = uv;
        vec4 wp = modelMatrix * vec4( position, 1.0 );
        vWorld = wp.xyz;
        vec4 mvPosition = viewMatrix * wp;
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <fog_pars_fragment>
      uniform float uWidth;
      varying vec3 vWorld;
      varying vec2 vUvP;
      ${FOCUS_DECL}
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float vnoise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        float a = hash(i), b = hash(i + vec2(1.0, 0.0));
        float c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
      }
      float fbm(vec2 p) {
        float v = 0.0, a = 0.5;
        for (int i = 0; i < 5; i++) { v += a * vnoise(p); p *= 2.02; a *= 0.5; }
        return v;
      }
      void main() {
        vec2 p = vWorld.zy;                          // across (z) + up (y) on the wall
        float relief = fbm(p * 9.0);
        float e = 0.015;
        vec2 grad = vec2(fbm((p + vec2(e, 0.0)) * 9.0) - relief,
                         fbm((p + vec2(0.0, e)) * 9.0) - relief);
        float key = dot(normalize(vec2(-0.7, 0.7)), grad);   // implied light from top-left
        float broad = fbm(p * 1.1);
        float tone = 0.94 + (broad - 0.5) * 0.06;            // broad value mottle
        tone += clamp(key * 5.5, -0.06, 0.06);               // faked relief highlights/shadows
        tone += (vnoise(p * 55.0) - 0.5) * 0.02;             // fine grain
        // soft edge falloff so each panel reads as a lit slab; grooves go darker
        float vx = smoothstep(0.0, 0.12, vUvP.x) * smoothstep(0.0, 0.12, 1.0 - vUvP.x);
        float vy = smoothstep(0.0, 0.06, vUvP.y) * smoothstep(0.0, 0.06, 1.0 - vUvP.y);
        tone *= mix(0.8, 1.0, vx * vy);

        // --- Implied ceiling spots: soft warm light pools down the climb ---
        // A vertical period of ~5.2u places one pool centred on each row of
        // works; the pool falls off top→bottom (light from above) and softly
        // across the wall width toward the lit centre.
        float PERIOD = 5.2;
        float yy = vWorld.y - 1.3;                   // first pool sits just above the foot
        float ph = (yy / PERIOD) - floor(yy / PERIOD);
        float dY = abs(ph - 0.42);                   // pool hot-spot biased to upper half
        float poolY = 1.0 - smoothstep(0.0, 0.5, dY);
        // across-wall falloff: brightest at centre of the wall span, but kept
        // gentle so the outer columns aren't double-darkened by the vignette
        float zc = vWorld.z / max(uWidth, 0.001);    // 0..1 across the wall
        float poolZ = 1.0 - smoothstep(0.34, 0.92, abs(zc - 0.5));
        float pool = pow(clamp(poolY * poolZ, 0.0, 1.0), 1.25);
        // dim the troughs between pools so the lit/unlit rhythm reads
        tone *= mix(0.78, 1.0, poolY);               // unlit gaps fall into shadow

        // --- P1: dynamic "follow-you" focus pool ---------------------------
        // A second, MOVING pool centred on the eased camera Y (uFocusY). The
        // wall plaster ignites near the viewer's height and the rest is pushed
        // hard toward shadow, giving the dark-gallery accent-to-surround ratio
        // (~1:15–1:25). We modulate the PLASTER tone only (boost wall, sink
        // troughs) — the art texture planes are separate meshes, so the mats
        // are never touched here. Blend is smooth (smoothstep, no hard band).
        float fp = focusPool(vWorld.y);              // 1 at viewer height → 0 away
        // Gentle contrast only: the surround eases toward shadow near the
        // viewer's pool, but the lit zone stays at/below unity — NO over-bright
        // plaster (over-unity + bloom was washing the art out). The art texture
        // planes are separate meshes, so the mats are never touched here.
        float surroundShadow = mix(0.5, 1.0, fp);
        surroundShadow = mix(1.0, surroundShadow, uFocusGain);
        tone *= surroundShadow;

        vec3 col = vec3(0.965, 0.968, 0.978) * tone;
        // faint warm tint in the pool centres (kept low so it doesn't bloom)
        col += vec3(0.022, 0.014, 0.005) * pool;
        col += vec3(0.016, 0.010, 0.004) * fp * uFocusGain;

        // --- P1: temperature journey across the climb ---------------------
        // cooler near the floor, warming gently as you climb — registers as
        // mood, not a tint on the art (plaster only). Keyed off absolute world
        // Y so it's stable as the viewer moves.
        float climb = clamp(vWorld.y / 26.0, 0.0, 1.0);
        col += vec3(0.020, 0.006, -0.010) * (climb - 0.5);

        gl_FragColor = vec4(col, 1.0);
        #include <fog_fragment>
      }`,
  });
}

/** Floor baseboard: dark plinth with a shadow gap on top + a cap highlight. */
function baseboardTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 8; c.height = 64;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#24242a'; ctx.fillRect(0, 0, 8, 64);  // body
  ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, 8, 2);   // shadow gap (meets panel)
  ctx.fillStyle = '#43434a'; ctx.fillRect(0, 2, 8, 3);   // cap highlight
  ctx.fillStyle = '#141416'; ctx.fillRect(0, 58, 8, 6);  // floor shadow
  return mkTex(c);
}

/** Build a raised white panel per cluster band (stacked up Y) + a baseboard at
 *  the foot of the wall. The ~0.8 horizontal grooves between bands read as
 *  shadow-gap reveals; each panel carries its own soft glow vignette. */
function addStructure(group: THREE.Group, bands: Band[], width: number): void {
  const panelMat = panelMaterial(width);  // shared procedural plaster across panels
  const panelW = width + 2 * PANEL_OVERHANG;
  for (const b of bands) {
    const yA = Math.max(0, b.yStart - PANEL_INSET_Y);
    const yB = b.yEnd + PANEL_INSET_Y;
    const h = Math.max(0.4, yB - yA);
    const panel = new THREE.Mesh(
      new THREE.PlaneGeometry(panelW, h),
      panelMat,
    );
    panel.rotation.y = Math.PI / 2;                       // normal +Z → +X
    panel.position.set(PANEL_X, (yA + yB) / 2, width / 2);
    panel.raycast = () => {};
    group.add(panel);
  }

  const board = new THREE.Mesh(
    new THREE.PlaneGeometry(panelW, BASEBOARD_TOP),
    new THREE.MeshBasicMaterial({ map: baseboardTexture(), fog: true }),
  );
  board.rotation.y = Math.PI / 2;
  board.position.set(BOARD_X, BASEBOARD_TOP / 2, width / 2);
  board.raycast = () => {};
  group.add(board);
}

export function buildWall(scene: THREE.Scene, manifest: Manifest): WallHandle {
  const layout = wallLayout(manifest);
  const group = new THREE.Group();
  const planes: WorkPlane[] = [];

  // Wall surface: the dark recess plane behind the panels (x=0 facing +X),
  // spanning Z (width) and Y (tall height).
  const surface = new THREE.Mesh(
    new THREE.PlaneGeometry(layout.width + 2, layout.height + 4),
    new THREE.MeshBasicMaterial({ map: wallRecessTexture(), fog: true }),
  );
  surface.rotation.y = Math.PI / 2;                 // normal +Z → +X
  surface.position.set(-0.05, layout.height / 2, layout.width / 2);
  surface.raycast = () => {};                        // never intercept picks
  group.add(surface);

  // Raised white panels per cluster (stacked up Y) + baseboard at the foot.
  addStructure(group, layout.bands, layout.width);

  const faceQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 2, 0));

  for (const p of layout.works) {
    const root = new THREE.Group();
    root.position.copy(p.position);
    root.quaternion.copy(faceQuat);
    root.scale.setScalar(p.scale);

    const L = p.layout;

    // Outer mat plane: the baked float shadow lives in its transparent margin.
    const cardMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(L.cardW, L.cardH),
      new THREE.MeshBasicMaterial({
        map: outerFrameTexture(p.work, pick(p.work.title)), transparent: true, fog: true,
      }),
    );
    root.add(cardMesh);

    // Artwork image plane, recessed in the window. Opacity 0 until lazy-loaded
    // so the baked empty-window recess shows through first.
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(L.winW, L.winH),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(p.work.palette[0]), transparent: true, opacity: 0, fog: true,
        polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1,
      }),
    );
    mesh.position.set(0, L.winCenterY, ART_Z);
    mesh.userData.restZ = ART_Z;
    mesh.renderOrder = 1;
    root.add(mesh);

    // Mount-ring overlay: keyline + baked inner contact shadow, on top of the art.
    const ringMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(L.winW, L.winH),
      new THREE.MeshBasicMaterial({
        map: ringTexture(p.work), transparent: true, fog: true,
        polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
      }),
    );
    ringMesh.position.set(0, L.winCenterY, RING_Z);
    ringMesh.renderOrder = 2;
    root.add(ringMesh);

    const plane: WorkPlane = {
      root, cardMesh, ringMesh, mesh, work: p.work,
      position: p.position.clone(),
      baseScale: root.scale.clone(),
      baseQuat: root.quaternion.clone(),
      state: 'empty',
    };
    cardMesh.userData.plane = plane;
    ringMesh.userData.plane = plane;
    mesh.userData.plane = plane;
    group.add(root);
    planes.push(plane);
  }

  scene.add(group);
  return { group, bands: layout.bands, planes, width: layout.width, height: layout.height };
}

/** Re-bake the outer card texture on language change (it carries the title).
 *  The ring overlay has no localised text, so it is not re-baked. */
export function refreshCardTextures(wall: WallHandle): void {
  for (const plane of wall.planes) {
    const mat = plane.cardMesh.material;
    const old = mat.map;
    mat.map = outerFrameTexture(plane.work, pick(plane.work.title));
    mat.needsUpdate = true;
    old?.dispose();
  }
}
